import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  Copy,
  Loader2,
  Package,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/PageHeader";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";
import type { AssetTemplate } from "@/components/TemplateSelector";

interface TemplateTask {
  id: string;
  template_id: string;
  title: string;
  description: string | null;
  trigger_type: "rrule" | "days_from_creation";
  rrule: string | null;
  trigger_offset_days: number | null;
  priority: string | null;
  estimated_minutes: number | null;
  category: string | null;
  sort_order: number;
}

interface TemplateInvLink {
  id: string;
  template_id: string;
  inventory_item_id: string | null;
  item_name: string;
  default_quantity: number;
  unit: string;
  consumption_trigger: "task_complete" | "manual";
  task_category: string | null;
  sort_order: number;
}

interface TemplateDetailResponse {
  template: AssetTemplate;
  tasks: TemplateTask[];
  inventory_links: TemplateInvLink[];
}

export function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<TemplateDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Task dialog state
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [taskTrigger, setTaskTrigger] = useState<"rrule" | "days_from_creation">("rrule");
  const [taskRrule, setTaskRrule] = useState("FREQ=MONTHLY");
  const [taskOffset, setTaskOffset] = useState("0");
  const [taskCategory, setTaskCategory] = useState("");
  const [taskPriority, setTaskPriority] = useState("medium");
  const [taskMinutes, setTaskMinutes] = useState("");
  const [taskSubmitting, setTaskSubmitting] = useState(false);

  // Inventory link dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkItemName, setLinkItemName] = useState("");
  const [linkInventoryItemId, setLinkInventoryItemId] = useState("");
  const [linkQuantity, setLinkQuantity] = useState("1");
  const [linkUnit, setLinkUnit] = useState("ea");
  const [linkTrigger, setLinkTrigger] = useState<"task_complete" | "manual">("task_complete");
  const [linkTaskCategory, setLinkTaskCategory] = useState("");
  const [linkSubmitting, setLinkSubmitting] = useState(false);

  const fetchDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const result = await authFetch<TemplateDetailResponse>(`/api/templates/${id}`);
      setData(result);
      setError("");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isSystem = data?.template.is_system === 1;
  const writable = !isSystem;

  const handleClone = async () => {
    if (!data) return;
    try {
      const cloned = await authFetch<AssetTemplate>(
        `/api/templates/${data.template.id}/clone`,
        { method: "POST", body: JSON.stringify({}) },
      );
      toast.success(`Cloned to "${cloned.name}"`);
      navigate(`/templates/${cloned.id}`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Clone failed";
      toast.error(msg);
    }
  };

  const handleAddTask = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    if (!taskTitle.trim()) {
      toast.error("Title required");
      return;
    }
    setTaskSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        title: taskTitle.trim(),
        description: taskDescription.trim() || undefined,
        trigger_type: taskTrigger,
        priority: taskPriority,
        category: taskCategory.trim() || undefined,
      };
      if (taskTrigger === "rrule") {
        body.rrule = taskRrule.trim();
      } else {
        body.trigger_offset_days = parseInt(taskOffset, 10) || 0;
      }
      if (taskMinutes) body.estimated_minutes = parseInt(taskMinutes, 10);

      await authFetch(`/api/templates/${data.template.id}/tasks`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      toast.success("Task added");
      setTaskDialogOpen(false);
      setTaskTitle("");
      setTaskDescription("");
      setTaskCategory("");
      setTaskMinutes("");
      await fetchDetail();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Add failed";
      toast.error(msg);
    } finally {
      setTaskSubmitting(false);
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!data) return;
    if (!confirm("Delete this task definition?")) return;
    try {
      await authFetch(`/api/templates/${data.template.id}/tasks/${taskId}`, {
        method: "DELETE",
      });
      toast.success("Task removed");
      await fetchDetail();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Delete failed";
      toast.error(msg);
    }
  };

  const handleAddLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    if (!linkItemName.trim() || !linkUnit.trim()) {
      toast.error("Item name and unit are required");
      return;
    }
    setLinkSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        item_name: linkItemName.trim(),
        default_quantity: parseFloat(linkQuantity) || 1,
        unit: linkUnit.trim(),
        consumption_trigger: linkTrigger,
        task_category: linkTaskCategory.trim() || undefined,
      };
      if (linkInventoryItemId.trim()) {
        body.inventory_item_id = linkInventoryItemId.trim();
      }
      await authFetch(`/api/templates/${data.template.id}/inventory-links`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      toast.success("Inventory link added");
      setLinkDialogOpen(false);
      setLinkItemName("");
      setLinkInventoryItemId("");
      setLinkQuantity("1");
      setLinkUnit("ea");
      setLinkTaskCategory("");
      await fetchDetail();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Add failed";
      toast.error(msg);
    } finally {
      setLinkSubmitting(false);
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    if (!data) return;
    if (!confirm("Remove this inventory link?")) return;
    try {
      await authFetch(
        `/api/templates/${data.template.id}/inventory-links/${linkId}`,
        { method: "DELETE" },
      );
      toast.success("Link removed");
      await fetchDetail();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Delete failed";
      toast.error(msg);
    }
  };

  const tasks = useMemo(() => data?.tasks ?? [], [data]);
  const links = useMemo(() => data?.inventory_links ?? [], [data]);

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[40vh]">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="container mx-auto p-6">
        <Card className="p-8 text-center">
          <p className="text-destructive">{error || "Template not found"}</p>
          <Link to="/templates" className="text-sm underline mt-3 inline-block">
            Back to templates
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-5xl">
      <PageHeader
        title={data.template.name}
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Templates", href: "/templates" },
          { label: data.template.name },
        ]}
      >
        <div className="flex items-center gap-2">
          {isSystem && (
            <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded bg-burnt-orange/10 text-burnt-orange dark:bg-burnt-orange-dark/15 dark:text-burnt-orange-dark">
              <Sparkles className="h-3 w-3" />
              System (read-only)
            </span>
          )}
          <Button onClick={handleClone} variant="outline" className="gap-2">
            <Copy className="h-4 w-4" />
            Clone
          </Button>
        </div>
      </PageHeader>

      {data.template.description && (
        <Card className="p-4 mb-6 bg-muted/30">
          <p className="text-sm">{data.template.description}</p>
        </Card>
      )}

      <div className="grid gap-2 sm:grid-cols-3 mb-8 text-sm">
        <Card className="p-3">
          <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
            Asset type
          </div>
          <div className="font-medium mt-1 capitalize">{data.template.asset_entity_type}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
            Tasks
          </div>
          <div className="font-medium mt-1">{tasks.length}</div>
        </Card>
        <Card className="p-3">
          <div className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
            Inventory links
          </div>
          <div className="font-medium mt-1">{links.length}</div>
        </Card>
      </div>

      {/* ── TASKS SECTION ── */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Recurring tasks
          </h2>
          {writable && (
            <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add task
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add task to template</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddTask} className="space-y-3">
                  <div className="space-y-2">
                    <Label>Title *</Label>
                    <Input
                      value={taskTitle}
                      onChange={(e) => setTaskTitle(e.target.value)}
                      required
                      placeholder="e.g. Oil change"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={taskDescription}
                      onChange={(e) => setTaskDescription(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Trigger</Label>
                    <Select
                      value={taskTrigger}
                      onValueChange={(v) =>
                        setTaskTrigger(v as "rrule" | "days_from_creation")
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="rrule">Recurring (RRULE)</SelectItem>
                        <SelectItem value="days_from_creation">
                          Once, N days after asset created
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {taskTrigger === "rrule" ? (
                    <div className="space-y-2">
                      <Label>RRULE *</Label>
                      <Input
                        value={taskRrule}
                        onChange={(e) => setTaskRrule(e.target.value)}
                        placeholder="FREQ=MONTHLY"
                      />
                      <p className="text-xs text-muted-foreground">
                        E.g. <code className="font-mono">FREQ=MONTHLY;INTERVAL=3</code> for
                        every 3 months.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Days after creation *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={taskOffset}
                        onChange={(e) => setTaskOffset(e.target.value)}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Priority</Label>
                      <Select value={taskPriority} onValueChange={setTaskPriority}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Est. minutes</Label>
                      <Input
                        type="number"
                        min="0"
                        value={taskMinutes}
                        onChange={(e) => setTaskMinutes(e.target.value)}
                        placeholder="30"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Input
                      value={taskCategory}
                      onChange={(e) => setTaskCategory(e.target.value)}
                      placeholder="maintenance, vet, vaccination…"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setTaskDialogOpen(false)}
                      disabled={taskSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={taskSubmitting}>
                      {taskSubmitting ? "Adding…" : "Add task"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {tasks.length === 0 ? (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            No task definitions yet.
          </Card>
        ) : (
          <Card className="divide-y">
            {tasks.map((t) => (
              <div key={t.id} className="p-4 flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">{t.title}</span>
                    {t.priority && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {t.priority}
                      </span>
                    )}
                    {t.category && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                        {t.category}
                      </span>
                    )}
                  </div>
                  {t.description && (
                    <p className="text-xs text-muted-foreground mt-1">{t.description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                    {t.trigger_type === "rrule" ? (
                      <span className="font-mono">{t.rrule}</span>
                    ) : (
                      <span>+{t.trigger_offset_days} days from creation</span>
                    )}
                    {t.estimated_minutes !== null && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {t.estimated_minutes}m
                      </span>
                    )}
                  </div>
                </div>
                {writable && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDeleteTask(t.id)}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </Card>
        )}
      </div>

      {/* ── INVENTORY LINKS SECTION ── */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Package className="h-4 w-4" />
            Inventory links
          </h2>
          {writable && (
            <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add link
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Add inventory link</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddLink} className="space-y-3">
                  <div className="space-y-2">
                    <Label>Item name *</Label>
                    <Input
                      value={linkItemName}
                      onChange={(e) => setLinkItemName(e.target.value)}
                      required
                      placeholder="15W-40 Engine Oil"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Inventory item ID (optional)</Label>
                    <Input
                      value={linkInventoryItemId}
                      onChange={(e) => setLinkInventoryItemId(e.target.value)}
                      placeholder="Leave blank for placeholder; user binds at apply"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Quantity *</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={linkQuantity}
                        onChange={(e) => setLinkQuantity(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unit *</Label>
                      <Input
                        value={linkUnit}
                        onChange={(e) => setLinkUnit(e.target.value)}
                        required
                        placeholder="ea, gal, qt, lb…"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Consumption trigger</Label>
                    <Select
                      value={linkTrigger}
                      onValueChange={(v) =>
                        setLinkTrigger(v as "task_complete" | "manual")
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="task_complete">
                          On task complete (auto-deduct)
                        </SelectItem>
                        <SelectItem value="manual">Manual only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Limit to task category (optional)</Label>
                    <Input
                      value={linkTaskCategory}
                      onChange={(e) => setLinkTaskCategory(e.target.value)}
                      placeholder="e.g. maintenance — leave blank for any task"
                    />
                  </div>
                  <DialogFooter>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setLinkDialogOpen(false)}
                      disabled={linkSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={linkSubmitting}>
                      {linkSubmitting ? "Adding…" : "Add link"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {links.length === 0 ? (
          <Card className="p-6 text-center text-sm text-muted-foreground">
            No inventory links yet.
          </Card>
        ) : (
          <Card className="divide-y">
            {links.map((l) => (
              <div key={l.id} className="p-4 flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm">{l.item_name}</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {l.default_quantity} {l.unit}
                    </span>
                    {l.inventory_item_id === null && (
                      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-burnt-orange/10 text-burnt-orange dark:bg-burnt-orange-dark/15 dark:text-burnt-orange-dark">
                        Placeholder
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                    <span>
                      {l.consumption_trigger === "task_complete"
                        ? "Deducts on task complete"
                        : "Manual only"}
                    </span>
                    {l.task_category && (
                      <>
                        <span>·</span>
                        <span>category: {l.task_category}</span>
                      </>
                    )}
                  </div>
                </div>
                {writable && (
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDeleteLink(l.id)}
                    className="h-8 w-8 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}
